<template>
  <div>
    <b-table
      ref="selectableTable"
      striped
      hover
      :items="services"
      :fields="fields"
      selectable
      responsive
      selected-variant="success"
      :select-mode="selectMode"
      @row-selected="onRowSelected"
      tbodyTrClass="cursor-pointer"
      :current-page="currentPage"
      :per-page="perPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :filter="filter"
      :filter-function="filterServices"
      @filtered="onFiltered"
    >

      <template v-slot:top-row="data">
          <b-td></b-td>
          <b-td width="200px">
            <multiselect
              v-model="filterPlace"
              :options="getPlaces"
              :searchable="true"
              :allow-empty="true"
              :close-on-select="true"
              :show-labels="false"
              track-by="id"
              label="name"
              :placeholder="$t('choose')">
              <template
                slot="singleLabel" slot-scope="{ option }">
                <strong>{{ option.name }}</strong>
              </template>
            </multiselect>
          </b-td>
          <b-td colspan="2">
            <multiselect
              v-model="filterGroup"
              :options="getServiceGroups"
              :searchable="false"
              :allow-empty="true"
              :close-on-select="true"
              :show-labels="false"
              track-by="id"
              label="name"
              :placeholder="$t('choose')">
              <template
                slot="singleLabel" slot-scope="{ option }">
                <strong>{{ option.name }}</strong>
              </template>
            </multiselect>
          </b-td>
          <b-td>
            <b-form-checkbox
              id="checkbox-1"
              v-model="includeDevices"
              name="checkbox-1"
            >
              {{ $t('include-devices') }}
            </b-form-checkbox>
          </b-td>
      </template>


      <template v-slot:head(name)="data">
        <span class="text-info">{{ data.label.toUpperCase() }}</span>
      </template>

      <template v-slot:head(selected)="data">
        <a href="#" @click.prevent="toggleSelectAllRows">&check;</a>
      </template>

      <template v-slot:cell(selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>

      <template v-slot:cell(icon)="{ item }">
        <service-icon-wrapper
          :style="{ height: '20px' }"
          :service="item"
        />
      </template>

    </b-table>

    <b-row>
      <b-col offset="3" cols="6">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
        />
      </b-col>
    </b-row>

    <p v-if="selectedCount" class="text-center mt-3">
      {{ $t('selected') }}: {{ selectedCount }}
    </p>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import ServiceIconMixin from '@/mixins/ServiceIconMixin';
import { SERVICE_TYPES } from '@/utils/variables';
import ServiceIconWrapper from '@/components/services/service/ServiceIconWrapper';

export default {
  name: 'ServicePicker',
  mixins: [
    ServiceIconMixin,
  ],
  props: {
    value: {
      required: false,
    },
    services: {
      required: true,
    },
    allowMultiple: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      totalRows: 1,
      currentValue: this.value,
      perPage: 10,
      currentPage: 1,
      sortBy: 'placeName',
      sortDesc: false,
      filterGroup: null,
      filterPlace: null,
      includeDevices: false,
      filter: null,
      fields: [
        { key: 'selected', label: this.$t('selected') },
        { key: 'placeName', label: this.$t('place'), sortable: true },
        { key: 'groupName', label: this.$t('group'), sortable: true },
        { key: 'icon', label: this.$t('icon') },
        { key: 'name', label: this.$t('name') },
      ],
    };
  },
  computed: {
    ...mapGetters('service-group', [
      'getServiceGroups',
    ]),
    ...mapGetters([
      'getPlaces',
    ]),
    content: {
      get() { return this.value; },
      set(newValue) {
        this.currentValue = newValue;
        this.$emit('input', this.currentValue);
      },
    },
    selectMode() {
      return this.allowMultiple ? 'range' : 'single';
    },
    selectedCount() {
      return this.content ? this.content.length : 0;
    },
  },
  methods: {
    onRowSelected(services) {
      this.content = services.map(service => service.id);
    },
    toggleSelectAllRows() {
      if (this.selectedCount === this.perPage) {
        this.$refs.selectableTable.clearSelected();
      } else {
        this.$refs.selectableTable.selectAllRows();
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    filterServices(item, filter) {
      let result = true;
      result = this.filterGroup ? result && item.groupId === this.filterGroup.id : result;
      result = this.filterPlace ? result && item.placeId === this.filterPlace.id : result;
      result = !this.includeDevices ? result && item.type === SERVICE_TYPES.SERVICE : result;
      return result;
    },
    updateFilter() {
      this.filter = `
        ${this.filterGroup ? this.filterGroup.id : null}:
        ${this.filterPlace ? this.filterPlace.id : null}:
        ${this.includeDevices}
      `;
    },
  },
  watch: {
    filterGroup() {
      this.updateFilter();
    },
    filterPlace() {
      this.updateFilter();
    },
    includeDevices() {
      this.updateFilter();
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.services.length;
    this.updateFilter();
  },
  components: {
    ServiceIconWrapper,
    Multiselect,
  },
};
</script>

<style scoped>
.multiselect__content-wrapper {
  position: relative !important;
}
</style>
